import { INTERPOLATION_DELAY_MS } from './events';

/**
 * Buffers recent snapshots and reconstructs smooth positions between them.
 *
 * THE PROBLEM: the server ticks at 20Hz, the canvas draws at ~60fps. Applying
 * each snapshot directly means every position is held for three frames, which
 * reads as stepping — and once a camera follows a player, that steps the whole
 * screen rather than one sprite.
 *
 * THE FIX: keep the last second of snapshots, render everything
 * INTERPOLATION_DELAY_MS in the past, and blend between the two snapshots that
 * bracket that moment. Trading a little latency for continuous motion is the
 * standard bargain, and 100ms is imperceptible for players you are not
 * controlling.
 *
 * THE LOCAL PLAYER IS EXEMPT. Delaying your own character would stack 100ms on
 * top of the existing input round trip and make the controls feel materially
 * worse — the exact opposite of the goal. `latestFor()` exists for that, and
 * the camera follows it, so remote players trail slightly while you never do.
 * That asymmetry is intentional and is what makes the trade work.
 *
 * Arrival time is stamped client-side rather than trusting a server clock:
 * comparing timestamps across machines would need clock synchronisation, and
 * the delay window already absorbs the jitter that introduces.
 */

/** Discard anything older than this. One second is far more than is ever read. */
const RETENTION_MS = 1000;

const lerp = (a, b, t) => a + (b - a) * t;

export default class SnapshotBuffer {
  constructor({ delayMs = INTERPOLATION_DELAY_MS } = {}) {
    this.delayMs = delayMs;
    /** @type {{ t: number, players: Map<string, object> }[]} oldest first */
    this.frames = [];
  }

  /**
   * Record a snapshot as having arrived at `now`.
   *
   * Keyed by SHORT ID (`i`) — the per-room integer the server assigns — so
   * lookups during interpolation are not a scan. The full user id never
   * appears in a snapshot; see events.js.
   */
  push(snapshot, now) {
    const players = new Map();
    (snapshot?.players || []).forEach((p) => players.set(p.i, p));

    this.frames.push({ t: now, players });

    // Prune. Without this the buffer grows forever at 20 entries/second.
    const cutoff = now - RETENTION_MS;
    while (this.frames.length > 2 && this.frames[0].t < cutoff) {
      this.frames.shift();
    }
  }

  get isEmpty() {
    return this.frames.length === 0;
  }

  /** Newest snapshot's players, as an array. Undelayed. */
  latest() {
    if (this.isEmpty) return [];
    return [...this.frames[this.frames.length - 1].players.values()];
  }

  /**
   * A single player at the newest known position, with no interpolation delay.
   * This is what the local player and the camera use.
   *
   * @param {number} shortId
   */
  latestFor(shortId) {
    if (this.isEmpty) return null;
    return this.frames[this.frames.length - 1].players.get(shortId) || null;
  }

  /**
   * Every player as they were `delayMs` ago, positions blended.
   *
   * @param {number} now - performance.now()
   */
  interpolated(now) {
    if (this.isEmpty) return [];

    const target = now - this.delayMs;
    const newest = this.frames[this.frames.length - 1];
    const oldest = this.frames[0];

    // Nothing new enough to interpolate toward — the connection stalled, or
    // this is the first 100ms after joining. Hold at the newest rather than
    // extrapolating: guessing forward overshoots and then snaps back, which
    // looks worse than a brief pause.
    if (target >= newest.t) return [...newest.players.values()];

    // Render time predates everything buffered. Only happens right after a
    // reset; show the oldest we have.
    if (target <= oldest.t) return [...oldest.players.values()];

    // Find the bracketing pair. Walking from the end is fastest because the
    // answer is almost always the last one or two frames.
    let before = oldest;
    let after = newest;
    for (let i = this.frames.length - 1; i > 0; i -= 1) {
      if (this.frames[i - 1].t <= target && target <= this.frames[i].t) {
        before = this.frames[i - 1];
        after = this.frames[i];
        break;
      }
    }

    const span = after.t - before.t;
    const alpha = span > 0 ? (target - before.t) / span : 1;

    // Iterate the NEWER frame: a player who has left should disappear, not
    // linger because they were still present in the older one. This is also
    // what makes interest management free on the client — a player who walks
    // out of range simply stops being in the newer frame, which is
    // indistinguishable from leaving, and correct either way.
    return [...after.players.values()].map((now_) => {
      const then = before.players.get(now_.i);

      // Present only in the newer frame — someone who just joined, or who just
      // crossed into interest range. Snapping is correct; lerping would slide
      // them in from wherever the previous entry happened to sit, which looks
      // like a teleport.
      if (!then) return now_;

      return {
        ...now_,
        x: lerp(then.x, now_.x, alpha),
        y: lerp(then.y, now_.y, alpha),
      };
    });
  }

  /** Drop everything. Used on disconnect so stale positions are not blended. */
  clear() {
    this.frames = [];
  }
}
